interface AppConfig {
  wsUrl: string;
  apiBase: string;
  devBugReport: boolean;
  encryptionEnabled: boolean;
  dispatcherUrl: string;
  bugReportSecret: string;
  debugBadger: boolean;
  polarPortalUrl: string;
  // NOTE: feature-shaped flags (recordings/voice/turn-grounding) moved to the
  // runtime catalog — see web/src/lib/featureFlags.ts + useFeature(). Only
  // config-shaped values (URLs, secrets, timeouts) remain build-time here.
  /**
   * Mirrors the backend `LVDR_ASR_MAX_WAIT` (in SECONDS — the Go value is a
   * duration string, e.g. "30m", so this is its second-count). It's the cap
   * the server waits for an ASR result before giving up. The recordings
   * watchdog derives its in-flight ceiling from this (+ a margin) so the
   * client and server stay aligned, and the transcription progress UI shows
   * the user the limit. Keep in sync with `LVDR_ASR_MAX_WAIT`. Default 1800
   * (30 min).
   */
  asrMaxWaitSecs: number;
  /**
   * Mirrors the backend `LVDR_RECORDINGS_MAX_DURATION_SECS` — the maximum
   * audio length (in seconds) the ASR pipeline accepts. The mic recorder
   * shows this as the "max length" next to the live timer and auto-stops at
   * it, so a long capture never fails server-side for being over-length.
   * Keep in sync with `LVDR_RECORDINGS_MAX_DURATION_SECS`. Default 14400 (4h).
   */
  recordingsMaxDurationSecs: number;
}

let cachedConfig: AppConfig | null = null;

export function getConfig(): AppConfig {
  if (cachedConfig) return cachedConfig;

  // Same-origin in every env: the FE always calls the host it was served from
  // (preprod↔preprod, prod↔prod). Derive from window.location so ONE build is
  // env-agnostic — no absolute NEXT_PUBLIC_API_BASE baked in. SSR/build has no
  // window → '' (relative), which the consumers' `${apiBase}/api/v1/...` handle.
  const apiBase = typeof window !== 'undefined' ? window.location.origin : '';
  // Default wsUrl: derive from current page origin (browser) or require env var (server)
  const wsUrl =
    process.env.NEXT_PUBLIC_WS_URL ||
    (typeof window !== 'undefined'
      ? `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws/v2`
      : '');

  if (!wsUrl) throw new Error('NEXT_PUBLIC_WS_URL is required');

  const devBugReport = process.env.NEXT_PUBLIC_DEV_BUG_REPORT === 'true';
  const encryptionEnabled = process.env.NEXT_PUBLIC_ENCRYPTION_ENABLED === 'true';
  const dispatcherUrl = process.env.NEXT_PUBLIC_DISPATCHER_URL ?? 'https://todo.lumavista.eu';
  const bugReportSecret = process.env.NEXT_PUBLIC_BUG_REPORT_SECRET ?? '';
  const debugBadger = process.env.NEXT_PUBLIC_DEBUG_BADGER === 'true';
  const polarPortalUrl = process.env.NEXT_PUBLIC_POLAR_PORTAL_URL ?? '';
  const parsedAsrMaxWait = Number(process.env.NEXT_PUBLIC_ASR_MAX_WAIT_SECS);
  const asrMaxWaitSecs =
    Number.isFinite(parsedAsrMaxWait) && parsedAsrMaxWait > 0 ? parsedAsrMaxWait : 1800;
  const parsedMaxDuration = Number(process.env.NEXT_PUBLIC_RECORDINGS_MAX_DURATION_SECS);
  const recordingsMaxDurationSecs =
    Number.isFinite(parsedMaxDuration) && parsedMaxDuration > 0 ? parsedMaxDuration : 14400;

  cachedConfig = {
    wsUrl,
    apiBase,
    devBugReport,
    encryptionEnabled,
    dispatcherUrl,
    bugReportSecret,
    debugBadger,
    polarPortalUrl,
    asrMaxWaitSecs,
    recordingsMaxDurationSecs,
  };
  return cachedConfig;
}

/**
 * Returns true only when the browser can actually reach the dispatcher.
 * Two conditions must both hold:
 *  1. No mixed-content block: HTTPS page → HTTP dispatcher is always blocked.
 *  2. The dispatcher host is reachable from this origin (localhost stays local;
 *     a LAN IP like 192.168.1.69 is only reachable when the page itself isn't
 *     served over HTTPS from a different origin).
 */
export function isDispatcherReachable(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const dispatcher = new URL(getConfig().dispatcherUrl);
    // Mixed-content check: HTTPS page can never fetch HTTP (browsers block it)
    if (window.location.protocol === 'https:' && dispatcher.protocol === 'http:') {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}
